import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useLayout } from '../../../../contexts/LayoutContext';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  Calendar,
  Users,
  Briefcase,
  ArrowRight,
  ArrowLeft,
  BookOpen
} from 'lucide-react';
import { resolveFeatureSections } from '../../../../utils/featureSections';
import './GetStarted.css';

const GetStarted = () => {
  const { lang } = useLanguage();
  const { settings } = useLayout();
  const navigate = useNavigate();
  const isAr = lang === 'ar';
  const sections = resolveFeatureSections(settings);
  const sectionRef = useRef(null);
  const numCards = 4;
  const [visibleCards, setVisibleCards] = useState(new Array(numCards).fill(false));

  // الـ card الأول يتبدل بناءً على ما إذا كانت Products معطلة
  const firstCard = sections.products
    ? {
        id: 'shop',
        icon: ShoppingBag,
        title: isAr ? 'تسوق الآن' : 'Shop Now',
        subtitle: isAr ? 'منتجات رقمية' : 'Digital Products',
        desc: isAr ? 'اكتشف منتجاتنا الرقمية المتميزة بضغطة زر واحدة' : 'Discover our premium digital products with one click',
        path: '/shop',
        image: 'https://plus.unsplash.com/premium_photo-1661675436186-dc0fe0eba15a?w=600&h=900&fit=crop',
        delay: 0
      }
    : {
        id: 'courses',
        icon: BookOpen,
        title: isAr ? 'اعرف المزيد' : 'Learn Now',
        subtitle: isAr ? 'الكورسات' : 'Courses',
        desc: isAr ? 'تعلم مهارات جديدة من خبرائنا بطريقة تفاعلية وممتعة' : 'Learn new skills from our experts in an interactive and engaging way',
        path: '/courses',
        image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=900&fit=crop',
        delay: 0
      };

  const cards = [
    firstCard,
  {
    id: 'consultation',
    icon: Calendar,
    title: isAr ? 'احجز استشارة' : 'Book Consultation',
    subtitle: isAr ? 'جلسات استشارية' : 'Consulting Sessions',
    desc: isAr ? 'احصل على استشارة مخصصة من خبرائنا لتطوير مشروعك' : 'Get personalized consultation from our experts to develop your project',
    path: '/consulting',
    // المدير بالنظارة في اجتماع استشاري
    image: 'https://plus.unsplash.com/premium_photo-1663047642288-3a36fe4603ec?w=600&h=900&fit=crop',
    delay: 150
  },
  {
    id: 'expert',
    icon: Users,
    title: isAr ? 'اعثر على خبير' : 'Find an Expert',
    subtitle: isAr ? 'المستقلين' : 'Freelancers',
    desc: isAr ? 'تواصل مع نخبة المستقلين المحترفين لتنفيذ مشاريعك' : 'Connect with elite professional freelancers to execute your projects',
    path: '/freelancers',
    // فريق عمل محترف - الصورة المعتمدة
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=900&fit=crop',
    delay: 300
  },
  {
    id: 'team',
    icon: Briefcase,
    title: isAr ? 'انضم لفريقنا' : 'Join Our Team',
    subtitle: isAr ? 'وظائف وشراكات' : 'jobs & Partnerships',
    desc: isAr ? 'انضم إلينا أو استكشف فرص الشراكة الاستراتيجية المثمرة' : 'Join us or explore fruitful strategic partnership opportunities',
    path: '/jobs',
    // رجلان بالبدلات الرسمية يتصافحان - شراكة/توظيف
    image: 'https://images.unsplash.com/photo-1752159400890-d906038f1b35?w=600&h=900&fit=crop',
    delay: 450
  }
];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            cards.forEach((card, index) => {
              setTimeout(() => {
                setVisibleCards(prev => {
                  const newState = [...prev];
                  newState[index] = true;
                  return newState;
                });
              }, card.delay);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="get-started" 
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* الخلفية المتحركة */}
      <div className="get-started__bg">
        <div className="get-started__orb get-started__orb--1" />
        <div className="get-started__orb get-started__orb--2" />
        <div className="get-started__orb get-started__orb--3" />
      </div>

      {/* العنوان */}
      <div className="get-started__header-float">
        <h2 className="get-started__title-float">
          {isAr ? 'ابدأ رحلتك معنا' : 'Get Started'}
        </h2>
        <p className="get-started__subtitle-float">
          {isAr 
            ? 'اختر الخدمة التي تناسبك وانطلق نحو النجاح'
            : 'Choose the service that suits you and launch into success'}
        </p>
      </div>

      {/* الشبكة 2x2 */}
      <div className="get-started__grid-2x2">
        {cards.map((card, index) => {
          const Icon = card.icon;
          const isVisible = visibleCards[index];
          
          return (
            <div
              key={card.id}
              className={`get-started__card-2x2 ${isVisible ? 'is-visible' : ''}`}
              style={{ '--card-index': index }}
              onClick={() => navigate(card.path)}
            >
              <div className="get-started__image-wrapper-2x2">
                <img 
                  src={card.image} 
                  alt={card.title}
                  className="get-started__image-2x2"
                  loading="lazy"
                />
                <div className="get-started__image-overlay-2x2" />
                
                <div className="get-started__content-float-2x2">
                  <div className="get-started__icon-float-2x2">
                    <Icon size={24} />
                  </div>
                  
                  <div className="get-started__text-float-2x2">
                    <span className="get-started__subtitle-float-2x2">
                      {card.subtitle}
                    </span>
                    <h3 className="get-started__title-card-2x2">
                      {card.title}
                    </h3>
                    <p className="get-started__desc-float-2x2">
                      {card.desc}
                    </p>
                  </div>

                  <div className="get-started__arrow-float-2x2">
                    {isAr ? <ArrowLeft size={20} /> : <ArrowRight size={20} />}
                  </div>
                </div>
              </div>

              <div className="get-started__glow-border-2x2" />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default GetStarted;